import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { chordSymbol } from '@/music/chords';
import { allAlternativeShapes } from '@/music/alternativeShapes';
import { spellVoicingMidis } from '@/music/chordSpelling';
import { sharpName } from '@/music/notes';
import {
  PIANO_COMP_FAMILIES,
  candidatesForCompChord,
  type PianoCompFamily,
} from '@/music/pianoComping';
import { PIANO_LEFT_HAND_FAMILIES } from '@/music/pianoLeftHand';
import { enumerateChordMelodyPiano } from '@/music/pianoMelody';
import {
  fretForMidi,
  fretsOnLayout,
  layoutOfFingering,
  windowStartFor,
} from '@/music/placedVoicing';
import {
  frettedMidi,
  STRING_LAYOUTS,
  topStringForFingering,
  type StringLayoutId,
} from '@/music/tuning';
import { enumerateGripAlternatives, type GripAlternative } from '@/music/voiceLeading';
import {
  fretSpan,
  gripFromFrets,
  voiceRows,
  voicingIdentity,
  voicingIdentityLabel,
} from '@/music/voicing';
import { voicingFamilyLabel } from '@/music/voicingFamilies';
import { isFretsPin, type VoicingPin } from '@/music/voicingPins';
import { getChordPlayer } from '@/audio/player';
import { useStore } from '@/state/store';
import { ChordDiagram } from './ChordDiagram';
import { Modal } from './Modal';
import { PianoKeyboardDiagram } from './PianoKeyboardDiagram';
import { PianoPlayHoldButton, PlayHoldButton } from './PlayHoldButton';
import { FretboardEditor, KeyboardEditor } from './VoicingEditors';
import { AlternativeShapeEditor } from './AlternativeShapes';
import {
  InspectorContext,
  isKeyboardChord,
  type InspectChord,
  type InspectKeyboardChord,
  type InspectPinContext,
  type Inspectable,
} from './inspectorContext';

const sameFrets = (a: (number | null)[], b: (number | null)[]) =>
  a.length === b.length && a.every((fret, i) => fret === b[i]);
const sameMidis = (a: number[], b: number[]) =>
  a.length === b.length && a.every((m, i) => m === b[i]);

type PinTab = 'similar' | 'library' | 'place';

const TAB_LABELS: Record<PinTab, string> = {
  similar: 'Similar',
  library: 'Library',
  place: 'Place notes',
};

/**
 * The pin picker's frame: the lane's alternatives for the chord in three
 * rungs — SIMILAR (the engine's own enumeration), LIBRARY (the wider
 * vocabulary: V-System spreads on the guitar, every comp family on the
 * piano) and PLACE NOTES (build the grip yourself) — with Reset when a pin is
 * in force. Choosing one PINS it (voicingPins.ts): the tune is never touched,
 * the engine re-fits the neighbours, and the sheet marks the grip as yours.
 */
function PinFrame({
  pin,
  close,
  tab,
  tabs,
  onTab,
  children,
}: {
  pin: InspectPinContext;
  close: () => void;
  tab: PinTab;
  tabs: PinTab[];
  onTab: (tab: PinTab) => void;
  children: ReactNode;
}) {
  const clearVoicingPin = useStore((s) => s.clearVoicingPin);
  return (
    <section className="pin-picker" data-testid="pin-picker" data-lane={pin.lane}>
      <div className="row pin-picker-head">
        <div className="segmented" role="tablist" aria-label="Voicings">
          {tabs.map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              className={tab === id ? 'active' : ''}
              aria-selected={tab === id}
              data-testid={`pin-tab-${id}`}
              onClick={() => onTab(id)}
            >
              {TAB_LABELS[id]}
            </button>
          ))}
        </div>
        {pin.pinned && (
          <button
            type="button"
            className="btn btn-sm"
            data-testid="pin-reset"
            onClick={() => {
              clearVoicingPin(pin.lane, pin.key);
              close();
            }}
          >
            Reset
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

const play = (fn: () => unknown) => {
  try {
    void fn();
  } catch {
    /* no audio */
  }
};

/** The guitar lanes' picker: the engine's grips, the library's spreads, and
 * the fretboard editor — melody-led chords keep the written note on top in
 * every rung (the lists carry only grips that do; the editor locks it). */
function GripPinPicker({ chord, close }: { chord: InspectChord; close: () => void }) {
  const pin = chord.pin!;
  const catalogue = chord.fingering.voicingFamily === 'joe-pass';
  const avoidB9 = useStore((s) => s.avoidB9);
  const pinVoicing = useStore((s) => s.pinVoicing);
  const customShapes = useStore((s) => s.alternativeShapes);
  const preferredAlternativeIds = useStore((s) => s.preferredAlternativeIds);
  const [tab, setTab] = useState<PinTab>('similar');
  const alternatives = useMemo(() => {
    const all = enumerateGripAlternatives(
      { ...pin.chord, stringSet: chord.stringSet },
      {
        avoidB9,
        alternativeShapes: catalogue ? [] : allAlternativeShapes(customShapes),
        preferredAlternativeIds,
        ...(catalogue ? { families: ['joe-pass' as const] } : {}),
      },
    );
    const rank = (a: GripAlternative) =>
      (a.writtenOctave ? 0 : 2) +
      (a.usable ? 0 : 1) +
      (a.fingering.stringSet === chord.stringSet ? 0 : 4);
    return all.sort((a, b) => rank(a) - rank(b));
  }, [pin.chord, avoidB9, chord.stringSet, catalogue, customShapes, preferredAlternativeIds]);
  const choose = (alt: GripAlternative) => {
    const record: VoicingPin = isFretsPin(alt.pin)
      ? { kind: 'guitar-frets', chordType: pin.chord.chordType, ...alt.pin }
      : { kind: 'guitar', chordType: pin.chord.chordType, ...alt.pin };
    pinVoicing(pin.lane, pin.key, record);
    play(() => getChordPlayer().playFingering(alt.fingering, alt.fingering.stringSet));
    close();
  };

  // The editor opens on the grip it was asked about.
  const [layout, setLayout] = useState<StringLayoutId>(() => layoutOfFingering(chord.fingering));
  const [frets, setFrets] = useState<(number | null)[]>(() =>
    fretsOnLayout(chord.fingering.frets, layoutOfFingering(chord.fingering)),
  );
  const [windowStart, setWindowStart] = useState(() => windowStartFor(chord.fingering.frets));
  const lockedTopMidi = pin.chord.targetTopMidi ?? null;
  const lockedTopString = catalogue ? topStringForFingering(chord.fingering) : undefined;
  const placed = useMemo(() => {
    const strings = STRING_LAYOUTS[layout].strings;
    const top =
      layout === 'joe-pass' && lockedTopString != null
        ? lockedTopString
        : strings[strings.length - 1];
    const withLock = [...frets];
    if (lockedTopMidi != null) withLock[top] = fretForMidi(top, lockedTopMidi);
    return gripFromFrets(chord.rootDisplay, pin.chord.chordType, withLock, layout);
  }, [frets, layout, lockedTopMidi, lockedTopString, chord.rootDisplay, pin.chord.chordType]);
  const usePlaced = () => {
    if (!placed) return;
    pinVoicing(pin.lane, pin.key, {
      kind: 'guitar-frets',
      chordType: pin.chord.chordType,
      root: pin.chord.root,
      frets: placed.frets,
      stringLayout: layout,
    });
    play(() => getChordPlayer().playFingering(placed, placed.stringSet));
    close();
  };

  const list = (items: GripAlternative[]) => (
    <div className="voicing-options pin-alternatives">
      {items.map((alt) => {
        const current = sameFrets(alt.fingering.frets, chord.fingering.frets);
        const label = voicingIdentityLabel(alt.fingering, chord.rootDisplay, chord.chordType);
        const register = alt.fingering.stringSet === 'upper' ? 'Upper' : 'Lower';
        const alternativeShape = alt.fingering.alternativeShape;
        const notes = [
          alt.library ?? alternativeShape?.name ?? null,
          alt.fingering.voicingFamily === 'joe-pass' ? null : register,
          alt.library
            ? null
            : alternativeShape
              ? alternativeShape.construction
                ? voicingFamilyLabel(alternativeShape.construction)
                : 'Alternative shape'
              : voicingFamilyLabel(alt.fingering.voicingFamily),
          !isFretsPin(alt.pin) && alt.pin.octaveShift === 12
            ? '8va'
            : !isFretsPin(alt.pin) && alt.pin.octaveShift === -12
              ? '8vb'
              : null,
          alt.usable ? null : 'b9',
        ]
          .filter(Boolean)
          .join(' · ');
        return (
          <button
            key={`${alternativeShape?.id ?? alt.library ?? 'standard'}:${alt.fingering.frets.join(',')}`}
            type="button"
            className={`voicing-option pin-alternative${current ? ' selected' : ''}${alt.usable ? '' : ' avoided'}`}
            data-testid="pin-alternative"
            data-current={current || undefined}
            data-library={alt.library || undefined}
            aria-pressed={current}
            aria-label={`${label}, ${notes}${current ? ', current' : ''}`}
            onClick={() => choose(alt)}
          >
            <ChordDiagram
              fingering={alt.fingering}
              rootDisplay={chord.rootDisplay}
              chordType={chord.chordType}
              stringSet={alt.fingering.stringSet}
              showTitle={false}
              showIntervals={false}
              ariaLabel={label}
            />
            {label}
            <br />
            <span className="muted">{notes}</span>
          </button>
        );
      })}
      {items.length === 0 && <p className="muted">No grips carry the written note here</p>}
    </div>
  );

  return (
    <PinFrame
      pin={pin}
      close={close}
      tab={tab}
      tabs={catalogue ? ['similar', 'place'] : ['similar', 'library', 'place']}
      onTab={setTab}
    >
      {tab === 'similar' && list(catalogue ? alternatives : alternatives.filter((a) => !a.library))}
      {tab === 'library' && list(alternatives.filter((a) => !!a.library))}
      {tab === 'place' && (
        <div className="pin-place">
          <FretboardEditor
            rootDisplay={chord.rootDisplay}
            chordType={pin.chord.chordType}
            layout={layout}
            onLayoutChange={(next) => {
              setLayout(next);
              setFrets(fretsOnLayout(chord.fingering.frets, next));
            }}
            frets={frets}
            onFretsChange={setFrets}
            windowStart={windowStart}
            onWindowStartChange={setWindowStart}
            lockedTopMidi={lockedTopMidi}
            lockedTopString={lockedTopString}
          />
          <div className="row pin-place-actions">
            <span className="muted" data-testid="pin-place-label">
              {placed
                ? voicingIdentityLabel(placed, chord.rootDisplay, pin.chord.chordType)
                : catalogue
                  ? 'Place at least two notes'
                  : 'Place at least two notes, the top string among them'}
            </span>
            <span className="row pin-place-buttons">
              <PlayHoldButton
                fingering={placed ?? chord.fingering}
                stringSet={placed?.stringSet ?? chord.stringSet}
                className="btn btn-sm"
                disabled={!placed}
                label=""
                testId="pin-place-play"
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                data-testid="pin-place-use"
                disabled={!placed}
                onClick={usePlaced}
              >
                Use this voicing
              </button>
            </span>
          </div>
        </div>
      )}
    </PinFrame>
  );
}

function VoicingInspector({ chord, close }: { chord: InspectChord; close: () => void }) {
  const customShapes = useStore((s) => s.alternativeShapes);
  const preferredIds = useStore((s) => s.preferredAlternativeIds);
  const setAlternativePreferred = useStore((s) => s.setAlternativePreferred);
  const [savingAlternative, setSavingAlternative] = useState(false);
  const [savedAlternativeId, setSavedAlternativeId] = useState(
    chord.fingering.alternativeShape?.id,
  );
  const [savedNotice, setSavedNotice] = useState(false);
  const savedAlternative = allAlternativeShapes(customShapes).find(
    (shape) => shape.id === savedAlternativeId,
  );
  const canSaveAlternative =
    chord.chordType != null && layoutOfFingering(chord.fingering) !== 'joe-pass';
  // A source shape can have an exact spelling without a supported formula.
  // Generic chromatic names would mislabel its #11 as b5 or its #9 as b3.
  const showTones = chord.chordType != null;
  const rows = voiceRows(chord.fingering, chord.rootDisplay, chord.stringSet, chord.chordType).map(
    (row) => ({ ...row, note: chord.source?.noteNames[row.stringIndex] ?? row.note }),
  );
  const identity = voicingIdentity(chord.fingering, chord.rootDisplay, chord.chordType);
  // Source names retain their printed enharmonics and sounding octaves. Use
  // pitch order, not string order: a wide grip can cross adjacent strings.
  const pitchedRows = chord.source
    ? rows
        .filter((row) => row.fret != null)
        .sort((a, b) => frettedMidi(a.stringIndex, a.fret!) - frettedMidi(b.stringIndex, b.fret!))
    : [];
  const topNote = pitchedRows.at(-1)?.note ?? identity.topNote;
  const bassNote = pitchedRows[0]?.note ?? identity.bassNote;
  const identityLabel = voicingIdentityLabel(chord.fingering, chord.rootDisplay, chord.chordType);
  const span = fretSpan(chord.fingering, chord.stringSet);
  const voiceLabel = (note: string | null, tone: string | null) =>
    [note, tone].filter(Boolean).join(' · ') || '—';

  return (
    <>
      <div className="modal-body">
        <div>
          <ChordDiagram
            fingering={chord.fingering}
            rootDisplay={chord.rootDisplay}
            chordType={chord.chordType}
            stringSet={chord.stringSet}
            title={`${chord.symbol} · ${identityLabel}`}
            showTitle={false}
            leadNote={chord.leadNote ?? null}
            showIntervals={showTones}
          />
          <PlayHoldButton
            fingering={chord.fingering}
            stringSet={chord.stringSet}
            className="btn btn-primary btn-sm"
            style={{ width: '100%', marginTop: 10 }}
          />
        </div>

        <div className="inspector">
          <h4>Voicing Inspector</h4>
          <dl>
            <dt>Chord</dt>
            <dd>{chord.symbol}</dd>
            <dt>Family</dt>
            <dd>
              {chord.fingering.alternativeShape
                ? `${chord.fingering.alternativeShape.construction ? voicingFamilyLabel(chord.fingering.alternativeShape.construction) : 'Alternative shape'} · ${chord.fingering.alternativeShape.name}`
                : voicingFamilyLabel(chord.fingering.voicingFamily)}
            </dd>
            <dt>Top voice</dt>
            <dd>{voiceLabel(topNote, identity.topTone)}</dd>
            <dt>Bass voice</dt>
            <dd>{voiceLabel(bassNote, identity.bassTone)}</dd>
            {identity.finishedInversion && (
              <>
                <dt>Inversion</dt>
                <dd>{identity.finishedInversion}</dd>
              </>
            )}
            <dt>Fret span</dt>
            <dd>
              {span} fret{span === 1 ? '' : 's'}
            </dd>
            {chord.targetTopNote && (
              <>
                <dt>Lead target</dt>
                <dd>{chord.targetTopNote}</dd>
              </>
            )}
            {chord.pin && (
              <>
                <dt>Voicing</dt>
                <dd data-testid="pin-state">{chord.pin.pinned ? 'Pinned' : 'Generated'}</dd>
              </>
            )}
            {chord.source && (
              <>
                <dt>Source</dt>
                <dd>{chord.source.reference}</dd>
              </>
            )}
          </dl>
          {chord.source?.description && <p className="muted">{chord.source.description}</p>}

          <table className="voice-table">
            <thead>
              <tr>
                <th>String</th>
                <th>Fret</th>
                <th>Note</th>
                {showTones && <th>Tone</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr className="voice-row" key={row.stringIndex}>
                  <td>{row.stringLabel}</td>
                  <td>{row.fret ?? '—'}</td>
                  <td>{row.note ?? '—'}</td>
                  {showTones && <td>{row.interval ?? '—'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {canSaveAlternative && (
        <section className="inspector-alternative">
          {savingAlternative ? (
            <>
              <h4>Save an alternative shape</h4>
              <AlternativeShapeEditor
                rootDisplay={chord.rootDisplay}
                chordType={chord.chordType!}
                initialFingering={chord.fingering}
                onClose={() => setSavingAlternative(false)}
                onSaved={(shape) => {
                  setSavedAlternativeId(shape.id);
                  setSavedNotice(true);
                }}
              />
            </>
          ) : (
            <>
              <div className="row">
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setSavingAlternative(true)}
                >
                  Save as alternative
                </button>
                {savedAlternative && (
                  <label className="alternative-shape-check">
                    <input
                      type="checkbox"
                      checked={preferredIds.includes(savedAlternative.id)}
                      onChange={(event) =>
                        setAlternativePreferred(savedAlternative.id, event.target.checked)
                      }
                    />
                    Prefer in arrangements
                  </label>
                )}
              </div>
              {savedNotice && (
                <p className="muted" role="status">
                  Shape saved to the library.
                </p>
              )}
            </>
          )}
        </section>
      )}
      {chord.pin && !savingAlternative && <GripPinPicker chord={chord} close={close} />}
    </>
  );
}

interface KeyboardAlternative {
  midis: number[];
  hands?: { left: number[]; right: number[] };
  family?: string;
  familyLabel?: string;
  inversion?: number;
}

/** The keyboard lanes' picker: the comping walk's own family (Similar), every
 * comp family the band offers (Library), and the keyboard editor; under the
 * melody lens the chord-melody stacks carrying the written note, and the
 * editor keeps that note on top. */
function KeyboardPinPicker({ chord, close }: { chord: InspectKeyboardChord; close: () => void }) {
  const pin = chord.pin!;
  const avoidB9 = useStore((s) => s.avoidB9);
  const pinVoicing = useStore((s) => s.pinVoicing);
  const melodyLane = pin.lane === 'melody.piano' && pin.chord.targetTopMidi != null;
  const [tab, setTab] = useState<PinTab>('similar');
  const source = pin.chord;
  const compChord = {
    root: source.root,
    chordType: source.chordType,
    displayRoot: source.displayRoot,
    ...(source.bass ? { bass: source.bass } : {}),
  };
  const dedupe = (list: KeyboardAlternative[]) => {
    const seen = new Set<string>();
    return list
      .filter((c) => {
        const key = c.midis.join(',');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.midis[a.midis.length - 1] - b.midis[b.midis.length - 1]);
  };
  const similar = useMemo<KeyboardAlternative[]>(() => {
    if (melodyLane) {
      return dedupe(
        enumerateChordMelodyPiano(
          { ...compChord, targetTopMidi: source.targetTopMidi! },
          { avoidB9 },
        ).map((c) => ({ midis: c.midis, hands: c.hands })),
      );
    }
    return dedupe(
      candidatesForCompChord(compChord, {
        family: pin.pianoFamily as PianoCompFamily | undefined,
        avoidB9,
      }).map((c) => ({
        midis: c.midis,
        hands: c.hands,
        family: c.family,
        inversion: c.inversion,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, avoidB9, melodyLane]);
  const library = useMemo<KeyboardAlternative[]>(() => {
    if (melodyLane) return [];
    const families = [...PIANO_COMP_FAMILIES, ...PIANO_LEFT_HAND_FAMILIES];
    const own = new Set(similar.map((c) => c.midis.join(',')));
    return dedupe(
      families.flatMap((family) =>
        candidatesForCompChord(compChord, { family: family.id, avoidB9 }).map((c) => ({
          midis: c.midis,
          hands: c.hands,
          family: c.family,
          familyLabel: family.label,
          inversion: c.inversion,
        })),
      ),
    ).filter((c) => !own.has(c.midis.join(',')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, avoidB9, melodyLane, similar]);
  const choose = (alt: KeyboardAlternative) => {
    pinVoicing(pin.lane, pin.key, {
      kind: 'piano',
      chordType: source.chordType,
      root: source.root,
      midis: alt.midis,
      ...(alt.hands && alt.hands.left.length ? { hands: alt.hands } : {}),
      ...(alt.family ? { family: alt.family } : {}),
      ...(alt.inversion != null ? { inversion: alt.inversion } : {}),
    });
    play(() => getChordPlayer().playMidi(alt.midis, 0.012, 'piano'));
    close();
  };
  const [placedMidis, setPlacedMidis] = useState<number[]>(() => chord.midis);
  const lockedTopMidi = melodyLane ? source.targetTopMidi! : null;
  const placedAll = useMemo(() => {
    const set = new Set(placedMidis);
    if (lockedTopMidi != null) set.add(lockedTopMidi);
    return [...set].sort((a, b) => a - b);
  }, [placedMidis, lockedTopMidi]);
  const usePlaced = () => {
    if (placedAll.length < 2) return;
    pinVoicing(pin.lane, pin.key, {
      kind: 'piano',
      chordType: source.chordType,
      root: source.root,
      midis: placedAll,
    });
    play(() => getChordPlayer().playMidi(placedAll, 0.012, 'piano'));
    close();
  };

  const list = (items: KeyboardAlternative[]) => (
    <div className="voicing-options pin-alternatives pin-alternatives-keyboard">
      {items.map((alt) => {
        const current = sameMidis(alt.midis, chord.midis);
        const names = spellVoicingMidis(alt.midis, chord.rootDisplay, chord.chordType, sharpName);
        return (
          <button
            key={alt.midis.join(',')}
            type="button"
            className={`voicing-option pin-alternative${current ? ' selected' : ''}`}
            data-testid="pin-alternative"
            data-current={current || undefined}
            data-library={alt.familyLabel || undefined}
            aria-pressed={current}
            aria-label={`${names.join(' ')}${alt.familyLabel ? `, ${alt.familyLabel}` : ''}${current ? ', current' : ''}`}
            onClick={() => choose(alt)}
          >
            <PianoKeyboardDiagram midis={alt.midis} height={36} label={names.join(' ')} />
            <span className="muted">
              {names.join(' ')}
              {alt.familyLabel ? ` · ${alt.familyLabel}` : ''}
            </span>
          </button>
        );
      })}
      {items.length === 0 && <p className="muted">Nothing else voices this chord</p>}
    </div>
  );

  return (
    <PinFrame
      pin={pin}
      close={close}
      tab={tab}
      tabs={melodyLane ? ['similar', 'place'] : ['similar', 'library', 'place']}
      onTab={setTab}
    >
      {tab === 'similar' && list(similar)}
      {tab === 'library' && list(library)}
      {tab === 'place' && (
        <div className="pin-place">
          <KeyboardEditor
            rootDisplay={chord.rootDisplay}
            chordType={source.chordType}
            midis={placedMidis}
            onMidisChange={setPlacedMidis}
            lockedTopMidi={lockedTopMidi}
          />
          <div className="row pin-place-actions">
            <span className="muted" data-testid="pin-place-label">
              {placedAll.length >= 2
                ? spellVoicingMidis(placedAll, chord.rootDisplay, chord.chordType, sharpName).join(
                    ' ',
                  )
                : 'Place at least two notes'}
            </span>
            <span className="row pin-place-buttons">
              <PianoPlayHoldButton
                midis={placedAll}
                className="btn btn-sm"
                disabled={placedAll.length < 2}
                label=""
                testId="pin-place-play"
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                data-testid="pin-place-use"
                disabled={placedAll.length < 2}
                onClick={usePlaced}
              >
                Use this voicing
              </button>
            </span>
          </div>
        </div>
      )}
    </PinFrame>
  );
}

function KeyboardInspector({ chord, close }: { chord: InspectKeyboardChord; close: () => void }) {
  const names = spellVoicingMidis(chord.midis, chord.rootDisplay, chord.chordType, sharpName);
  const hands = chord.hands;
  return (
    <>
      <div className="modal-body">
        <div>
          <PianoKeyboardDiagram midis={chord.midis} height={64} label={`${chord.symbol} voicing`} />
          {/* The guitar inspector's Play: ▶, tap to sound, hold to roll. */}
          <PianoPlayHoldButton
            midis={chord.midis}
            className="btn btn-primary btn-sm"
            style={{ width: '100%', marginTop: 10 }}
          />
        </div>
        <div className="inspector">
          <h4>Voicing Inspector</h4>
          <dl>
            <dt>Chord</dt>
            <dd>{chord.symbol}</dd>
            <dt>Notes</dt>
            <dd>{names.join(' ')}</dd>
            {hands && hands.left.length > 0 && (
              <>
                <dt>Left hand</dt>
                <dd>
                  {spellVoicingMidis(
                    hands.left,
                    chord.rootDisplay,
                    chord.chordType,
                    sharpName,
                  ).join(' ')}
                </dd>
                <dt>Right hand</dt>
                <dd>
                  {spellVoicingMidis(
                    hands.right,
                    chord.rootDisplay,
                    chord.chordType,
                    sharpName,
                  ).join(' ')}
                </dd>
              </>
            )}
            {chord.pin && (
              <>
                <dt>Voicing</dt>
                <dd data-testid="pin-state">{chord.pin.pinned ? 'Pinned' : 'Generated'}</dd>
              </>
            )}
          </dl>
        </div>
      </div>
      {chord.pin && <KeyboardPinPicker chord={chord} close={close} />}
    </>
  );
}

export function InspectorProvider({ children }: { children: ReactNode }) {
  const [chord, setChord] = useState<Inspectable | null>(null);
  const inspect = useCallback((c: Inspectable) => setChord(c), []);
  const close = useCallback(() => setChord(null), []);
  const api = useMemo(() => ({ inspect }), [inspect]);

  return (
    <InspectorContext.Provider value={api}>
      {children}
      {chord && (
        <Modal
          title={
            chord.title ??
            (isKeyboardChord(chord)
              ? chord.symbol || chordSymbol(chord.rootDisplay, chord.chordType)
              : `${chord.symbol} · ${voicingIdentityLabel(chord.fingering, chord.rootDisplay, chord.chordType)}`)
          }
          onClose={close}
        >
          {isKeyboardChord(chord) ? (
            <KeyboardInspector chord={chord} close={close} />
          ) : (
            <VoicingInspector chord={chord} close={close} />
          )}
        </Modal>
      )}
    </InspectorContext.Provider>
  );
}
